@use 'sass:math';

@use '../../scss/utility';

$_padding: 1rem;
$_defaut-icon-size: 24px;

:host {
  --active-color: var(--wb-blue-45);
  --error-color: var(--wb-red-45);
  --success-color: var(--wb-green-45);

  display: block;
}

.inner {
  position: relative;
  height: 3.5rem;
  border: 1px solid var(--wb-grey-70);
  background-color: var(--wb-grey-95);
  box-sizing: border-box;

  // status lines: initial state
  // ::before for the states like filled, success and error, ::after for the focus border
  &::after,
  &::before {
    content: ' ';
    position: absolute;
    inset-block-end: -1px;
    inset-inline: -1px;
    transform: scale(0);
    transform-origin: bottom left;
    border-block-end: 2px solid transparent;
    transition: all 0.5s var(--wb-fade);
  }
}

:host([is-rtl]) .inner {
  &::after,
  &::before {
    transform-origin: bottom right;
  }
}

.label {
  @include utility.wb-text(
    's',
    (
      responsive: false,
    )
  );
  cursor: text;
  background-color: inherit;
  color: var(--wb-grey-20);
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  padding-block-start: 0.5rem;
  padding-inline-end: 2.75rem;
  padding-block-end: 0;
  padding-inline-start: $_padding;
  pointer-events: none;
  position: absolute;
  inset-block-start: 0;
  width: 100%;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

::slotted(select) {
  @include utility.wb-text(
    'l',
    (
      responsive: false,
    )
  );
  color: var(--wb-grey-20);
  outline: none;
  border: none;
  background-color: inherit;
  text-overflow: ellipsis;
  overflow: hidden;
  height: 3.4rem;
  width: 100%;
  padding-block-start: 1.5rem;
  padding-inline-end: 2.75rem;
  padding-block-end: 0.5rem;
  padding-inline-start: $_padding;
  text-transform: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  @supports (-moz-appearance: none) {
    text-indent: -2px;
  }
}

option {
  color: var(--wb-grey-20);
}

// have to set the color twice to enable color-change in all browsers.
:host([value]:not([value=''])) ::slotted(select) {
  color: var(--wb-grey-20);
}

:host(:not([disabled])):hover {
  background-color: var(--wb-white);
}

:host([disabled]) {
  opacity: 0.4;
  cursor: not-allowed;

  .label {
    cursor: not-allowed;
  }
}

:host(:not([filled])) ::slotted(select) {
  color: var(--wb-grey-40);
}

:host([disabled]) ::slotted(select) {
  cursor: not-allowed;
}

:host([active]) .inner::after {
  border-color: var(--active-color);
  transform: scale(1);
}

:host([active]) ::slotted(select),
::slotted(select:focus) {
  color: var(--wb-grey-20);
}

:host([success]),
:host([error]) {
  .label {
    padding-inline-end: 5rem;
  }
}

:host([success]) ::slotted(select),
:host([error]) ::slotted(select) {
  padding-inline-end: 5rem;
}

:host([success]) .inner::before {
  border-color: var(--success-color);
  transform: scale(1);
}

:host([error]) .inner::before {
  border-color: var(--error-color);
  transform: scale(1);
}

:host([compact]) {
  display: inline-flex;
  flex-direction: column;
  min-width: 144px;

  .label {
    position: absolute;
    opacity: 0;
  }
}

:host([compact]) ::slotted(select) {
  padding-block-start: 14px;
  padding-block-end: 14px;
}

::slotted(.wb-control-error),
::slotted(.wb-control-hint) {
  padding-inline-start: $_padding;
}

.icon {
  position: absolute;
  pointer-events: none;
  inset-block-start: $_padding;
  inset-inline-end: $_padding;
}

.success-icon {
  color: var(--success-color);
}

.error-icon {
  color: var(--error-color);
}

.caret-icon {
  color: var(--wb-grey-20);
  box-sizing: content-box;
  width: 24px;
  height: 24px;
}

:host([success]),
:host([error]) {
  .caret-icon {
    inset-inline-end: 3rem;
  }
}

:host([theme='dark']) {
  --active-color: var(--wb-blue-50);
  --error-color: var(--wb-red-50);
  --success-color: var(--wb-green-50);

  .inner {
    background-color: var(--wb-grey-15);
    border-color: var(--wb-grey-40);
  }

  .label {
    color: var(--wb-white);
  }

  option {
    color: var(--wb-grey-80);
  }

  .caret-icon {
    color: var(--wb-grey-70);
  }
}

:host([theme='dark']:not([filled])) ::slotted(select) {
  color: var(--wb-grey-55);
}

:host([theme='dark']) ::slotted(select) {
  color: var(--wb-white);
  background-color: var(--wb-grey-15);
}

:host([theme='dark'][value]:not([value=''])) ::slotted(select) {
  color: var(--wb-white);
}

:host([theme='dark']:not([disabled])):hover {
  background-color: var(--wb-black);
}

:host([theme='dark'][active]) ::slotted(select),
:host([theme='dark']) ::slotted(select:focus) {
  color: var(--wb-white);
}
